<template>
    <div class="donut-breakdown" :class="{ 'donut-breakdown--column': column }">
        <div class="donut-chart" :style="chartStyle">
            <doughnut-chart v-if="hasData" :labels="labels" :datasets="datasets" :center-lines="centerLines"
                :width="size" :height="size" />
            <div v-else class="donut-placeholder" />
        </div>
        <div class="donut-legend">
            <div v-for="item in items" :key="item.label" class="legend-row">
                <span class="legend-dot" :style="{ background: item.color }" />
                <span class="legend-label">{{ item.label }}</span>
                <span class="legend-value">{{ item.text || percent(item) }}</span>
            </div>
            <span v-if="!hasData" class="legend-empty">{{ emptyText || $t('common.without_data') }}</span>
        </div>
    </div>
</template>

<script>
import DoughnutChart from "@/components/common/DoughnutChart";

export default {
    name: "DonutBreakdown",
    components: { DoughnutChart },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        size: {
            type: [String, Number],
            default: 100,
        },
        centerValue: {
            type: String,
            default: '',
        },
        centerLabel: {
            type: String,
            default: '',
        },
        centerColor: {
            type: String,
            default: '',
        },
        column: {
            type: Boolean,
            default: false,
        },
        emptyText: {
            type: String,
            default: '',
        },
    },
    computed: {
        total(vm = this) {
            return vm.items.reduce((sum, item) => sum + (Number(item.value) || 0), 0)
        },
        hasData(vm = this) {
            return vm.total > 0
        },
        labels(vm = this) {
            return vm.items.map(item => item.label)
        },
        datasets(vm = this) {
            return [{
                data: vm.items.map(item => Number(item.value) || 0),
                backgroundColor: vm.items.map(item => item.color),
            }]
        },
        chartStyle(vm = this) {
            return { width: `${vm.size}px`, height: `${vm.size}px` }
        },
        centerLines(vm = this) {
            const lines = []
            if (vm.centerValue) {
                lines.push({
                    text: vm.centerValue,
                    fontSize: 15,
                    fontWeight: 700,
                    color: vm.centerColor || vm.$vuetify.theme.currentTheme.txtPrimary,
                })
            }
            if (vm.centerLabel) {
                lines.push({
                    text: vm.centerLabel,
                    fontSize: 9,
                    fontWeight: 600,
                    color: vm.$vuetify.theme.currentTheme.txtMuted,
                })
            }
            return lines
        },
    },
    methods: {
        percent(item) {
            const vm = this
            if (!vm.total) return '0%'
            return `${Math.round((Number(item.value) || 0) / vm.total * 100)}%`
        },
    },
}
</script>

<style scoped>
.donut-breakdown {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex: 1 1 auto;
}

.donut-breakdown--column {
    flex-direction: column;
    align-items: stretch;
}

.donut-chart {
    position: relative;
    flex-shrink: 0;
    align-self: center;
}

.donut-placeholder {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 10px solid var(--v-surface2-base);
}

.donut-legend {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1 1 auto;
    min-width: 0;
}

.legend-row {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
}

.legend-dot {
    width: 7px;
    height: 7px;
    border-radius: 2px;
    flex-shrink: 0;
}

.legend-label {
    font-size: var(--fs-caption);
    color: var(--v-txtSecondary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.legend-value {
    margin-left: auto;
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    flex-shrink: 0;
}

.legend-empty {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}
</style>
